class CustomRegisterForm extends HTMLElement {
  constructor() {
    super();

    this.cnpjInput = null;
    this.isLoading = false;
    this.lastCnpj = "";
    this.abortController = null;

    this.loadingClass = "is-loading";
    this.filledClass = "is-filled-by-cnpj";
    this.lockedClass = "is-locked-by-cnpj";
  }

  connectedCallback() {
    this.cnpjInput = this.findField("CNPJ");

    if (!this.cnpjInput) {
      console.warn("[CustomRegisterForm] Campo CNPJ não encontrado.");
      return;
    }

    this.cnpjInput.addEventListener("input", this.handleCnpjInput.bind(this));
    this.cnpjInput.addEventListener("blur", this.handleCnpjBlur.bind(this));

    const resetButton = this.querySelector("[data-cnpj-reset]");

    if (resetButton) {
      resetButton.addEventListener("click", this.handleCnpjReset.bind(this));
    }

    const updatableInputs = this.querySelectorAll(
      "#e-mail, #ponto-de-referencia"
    );

    if (updatableInputs) {
      updatableInputs.forEach((input) => {
        input.addEventListener(
          "input",
          this.handleUpdatableInputChange.bind(this)
        );
      });
    }
  }

  handleUpdatableInputChange(e) {
    const tags = this.querySelector("[data-customer-tags]");
    const tagArray = tags.value.split(",");
    const updatedTags = tagArray
      .map((keyValuePair) => {
        const [key, value] = keyValuePair.split(":");
        if (key == e.target.id) {
          return `${key}: ${e.target.value}`;
        } else {
          return keyValuePair;
        }
      })
      .join(",");

    tags.value = updatedTags;
  }

  /**
   * Obtém o texto do label associado a um campo.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} field - Campo do formulário.
   * @returns {string} Texto do label associado ao campo.
   */
  getFieldLabel(field) {
    if (!field.id) return "";

    const label = this.querySelector(`label[for="${field.id}"]`);

    return label ? label.textContent.trim() : "";
  }

  /**
   * Reseta o campo de CNPJ e limpa os dados preenchidos automaticamente.
   *
   * @returns {void}
   */
  handleCnpjReset() {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.isLoading = false;
    this.lastCnpj = "";

    this.setLoading(false);
    this.unlockFields();
    this.clearFilledValues();
    this.clearFilledClasses();
    this.clearLockedClasses();

    if (this.cnpjInput) {
      this.cnpjInput.value = "";
      this.cnpjInput.focus();

      this.cnpjInput.dispatchEvent(new Event("input", { bubbles: true }));
      this.cnpjInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  /**
   * Limpa os valores dos campos preenchidos automaticamente pelo CNPJ.
   *
   * @returns {void}
   */
  clearFilledValues() {
    const filledWrappers = this.querySelectorAll(`.${this.filledClass}`);

    filledWrappers.forEach((wrapper) => {
      const field = wrapper.matches("input, textarea, select")
        ? wrapper
        : wrapper.querySelector("input, textarea, select");

      if (!field) return;

      field.value = "";

      /* field.dispatchEvent(new Event("input", { bubbles: true }));
      field.dispatchEvent(new Event("change", { bubbles: true })); */
    });

    const tags = this.querySelector("[data-customer-tags]");
    tags.value = "";
  }

  /**
   * Manipula a digitação no campo de CNPJ.
   *
   * @param {InputEvent} event - Evento de input do campo de CNPJ.
   * @returns {void}
   */
  handleCnpjInput(event) {
    const input = event.target;

    this.unlockFields();
    this.clearFilledClasses();
    this.clearLockedClasses();

    input.value = this.formatCnpj(input.value);

    const cnpj = this.onlyNumbers(input.value);

    if (cnpj.length === 14) {
      this.fetchCompanyData(cnpj);
    }
  }

  /**
   * Manipula o evento de perda de foco do campo de CNPJ.
   *
   * @param {FocusEvent} event - Evento de blur do campo de CNPJ.
   * @returns {void}
   */
  handleCnpjBlur(event) {
    const cnpj = this.onlyNumbers(event.target.value);

    if (cnpj.length === 14) {
      this.fetchCompanyData(cnpj);
    }
  }

  /**
   * Consulta os dados da empresa a partir de um CNPJ.
   *
   * @param {string} cnpj - CNPJ contendo apenas números.
   * @returns {Promise<void>}
   */
  async fetchCompanyData(cnpj) {
    if (this.isLoading || this.lastCnpj === cnpj) return;

    this.isLoading = true;
    this.lastCnpj = cnpj;
    this.setLoading(true);

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      const response = await fetch(`https://open.cnpja.com/office/${cnpj}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Erro na consulta do CNPJ. Status: ${response.status}`);
      }

      const data = await response.json();

      console.log("[CustomRegisterForm] Dados retornados:", data);

      this.unlockFields();
      this.clearFilledClasses();
      this.clearLockedClasses();

      this.fillFields(data);
      this.lockFilledFields();
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("[CustomRegisterForm] Erro ao consultar CNPJ:", error);

        this.unlockFields();
        this.clearFilledClasses();
        this.clearLockedClasses();
        this.lastCnpj = "";
      }
    } finally {
      this.isLoading = false;
      this.setLoading(false);
    }
  }

  /**
   * Define o estado visual de carregamento do formulário.
   *
   * @param {boolean} isLoading - Indica se o formulário está em carregamento.
   * @returns {void}
   */
  setLoading(isLoading) {
    this.classList.toggle(this.loadingClass, isLoading);

    const submitButton = this.querySelector(
      'button[type="submit"], .b2b-form__submit button'
    );

    if (submitButton) {
      submitButton.disabled = isLoading;
    }
  }

  /**
   * Remove a classe de campos preenchidos automaticamente.
   *
   * @returns {void}
   */
  clearFilledClasses() {
    const fields = this.querySelectorAll(`.${this.filledClass}:has(#cnpj)`);

    fields.forEach((field) => {
      field.classList.remove(this.filledClass);
    });
  }

  /**
   * Remove a classe de campos bloqueados automaticamente.
   *
   * @returns {void}
   */
  clearLockedClasses() {
    const fields = this.querySelectorAll(`.${this.lockedClass}`);

    fields.forEach((field) => {
      field.classList.remove(this.lockedClass);
    });
  }

  /**
   * Bloqueia os campos que foram preenchidos automaticamente.
   *
   * @returns {void}
   */
  lockFilledFields() {
    const fields = this.querySelectorAll(
      `.${this.filledClass}:not(:has(#e-mail))`
    );

    fields.forEach((fieldWrapper) => {
      const field = fieldWrapper.matches("input, textarea, select")
        ? fieldWrapper
        : fieldWrapper.querySelector("input, textarea, select");

      if (!field) return;

      field.readOnly = true;

      if (field.tagName === "SELECT") {
        field.disabled = true;
      }

      /* fieldWrapper.classList.add(this.lockedClass); */
    });
  }

  /**
   * Desbloqueia todos os campos do formulário.
   *
   * @returns {void}
   */
  unlockFields() {
    const fields = this.querySelectorAll("input, textarea, select");

    fields.forEach((field) => {
      field.readOnly = false;

      if (field.tagName === "SELECT") {
        field.disabled = false;
      }
    });
  }

  /**
   * Preenche os campos do formulário com os dados retornados da consulta de CNPJ.
   *
   * @param {Object} data - Dados retornados pela API de CNPJ.
   * @returns {void}
   */
  fillFields(data) {
    const corporateName = data?.company?.name || data?.name || "";

    const tradeName = data?.alias || "";

    const email = data?.emails?.[0]?.address || data?.email || "";

    const phone1 =
      this.formatPhoneObject(data?.phones?.[0]) || data?.phone || "";

    const phone2 = this.formatPhoneObject(data?.phones?.[1]) || "";

    const address = data?.address || {};

    const zip = address?.zip || address?.postalCode || "";

    const street = address?.street || "";

    const number = address?.number || "";

    const details = address?.details || address?.complement || "";

    const district = address?.district || address?.neighborhood || "";

    const city = address?.city || "";

    const state = address?.state || "";

    const country = address?.country?.name || address?.country || "Brasil";

    const members = Array.isArray(data?.company?.members)
      ? data.company.members
      : Array.isArray(data?.members)
        ? data.members
        : [];

    const membersText = members
      .map((member) => {
        const name = member?.person?.name || member?.name || "";
        const role = member?.role?.text || member?.role || "";

        return [name, role].filter(Boolean).join(" - ");
      })
      .filter(Boolean)
      .join(", ");

    this.setFieldValue("Razão Social", corporateName);
    this.setFieldValue("Nome fantasia", tradeName);
    this.setFieldValue("E-mail", email);
    this.setFieldValue("Telefone 1", phone1);
    this.setFieldValue("Telefone 2", phone2);
    this.setFieldValue("CEP", this.formatCep(zip));
    this.setFieldValue("Logradouro", street);
    this.setFieldValue("Número", number);
    this.setFieldValue("Ponto de Referência", details);
    this.setFieldValue("Bairro", district);
    this.setFieldValue("Cidade", city);
    this.setFieldValue("UF", state);
    this.setFieldValue("País", country);
    this.setFieldValue("Quadro Societário", membersText);

    const customerTags = this.querySelector("[data-customer-tags]");
    customerTags.value =
      `b2b-pendente,cnpj:${data.taxId},razao-social:${corporateName},nome-fantasia:${tradeName},e-mail:${email},` +
      `tel-1:${phone1},tel-2:${phone2},cep:${zip},logradouro:${street},numero:${number},ponto-de-referencia:${details},bairro:${district},` +
      `cidade:${city},uf:${state},pais:${country},quadro-societario:${membersText.replaceAll(",", "|")}`;

    if (this.cnpjInput?.parentElement) {
      this.cnpjInput.parentElement.classList.add(this.filledClass);
    }
  }

  /**
   * Busca um campo do formulário com base no texto do label ou atributos relacionados.
   *
   * @param {string} labelText - Texto usado para localizar o campo.
   * @returns {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} Campo encontrado ou null.
   */
  findField(labelText) {
    const normalizedLabel = this.normalize(labelText);

    const fields = this.querySelectorAll("input, textarea, select");

    for (const field of fields) {
      const possibleValues = [
        field.name,
        field.id,
        field.getAttribute("aria-label"),
        field.getAttribute("placeholder"),
        field.dataset.name,
        field.dataset.field,
      ];

      const hasMatch = possibleValues.some((value) => {
        return value && this.normalize(value).includes(normalizedLabel);
      });

      if (hasMatch) {
        return field;
      }

      const label = this.querySelector(`label[for="${field.id}"]`);

      if (
        label &&
        this.normalize(label.textContent).includes(normalizedLabel)
      ) {
        return field;
      }
    }

    return null;
  }

  /**
   * Define o valor de um campo encontrado pelo texto do label.
   *
   * @param {string} labelText - Texto usado para localizar o campo.
   * @param {string} value - Valor a ser definido no campo.
   * @returns {void}
   */
  setFieldValue(labelText, value) {
    if (value === undefined || value === null || value === "") return;

    const field = this.findField(labelText);

    if (!field) {
      console.warn(`[CustomRegisterForm] Campo não encontrado: ${labelText}`);
      return;
    }

    field.value = value;

    if (field.id != "e-mail") {
      if (field.parentElement) {
        field.parentElement.classList.add(this.filledClass);
      } else {
        field.classList.add(this.filledClass);
      }
    }

    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Remove todos os caracteres não numéricos de uma string.
   *
   * @param {string|number|null|undefined} value - Valor a ser tratado.
   * @returns {string} Valor contendo apenas números.
   */
  onlyNumbers(value) {
    return String(value || "").replace(/\D/g, "");
  }

  /**
   * Normaliza uma string removendo acentos, espaços, símbolos e convertendo para minúsculas.
   *
   * @param {string|null|undefined} value - Valor a ser normalizado.
   * @returns {string} Valor normalizado.
   */
  normalize(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  /**
   * Formata um valor como CNPJ.
   *
   * @param {string|number|null|undefined} value - Valor a ser formatado.
   * @returns {string} CNPJ formatado.
   */
  formatCnpj(value) {
    const cnpj = this.onlyNumbers(value).slice(0, 14);

    return cnpj
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }

  /**
   * Formata um valor como CEP.
   *
   * @param {string|number|null|undefined} value - Valor a ser formatado.
   * @returns {string} CEP contendo até 8 números.
   */
  formatCep(value) {
    const cep = this.onlyNumbers(value).slice(0, 8);

    return cep;
  }

  /**
   * Formata um objeto de telefone retornado pela API.
   *
   * @param {Object|null|undefined} phone - Objeto contendo dados do telefone.
   * @param {string|number} [phone.area] - Código de área do telefone.
   * @param {string|number} [phone.ddd] - Código DDD alternativo do telefone.
   * @param {string|number} [phone.number] - Número do telefone.
   * @returns {string} Telefone formatado.
   */
  formatPhoneObject(phone) {
    if (!phone) return "";

    const area = phone.area || phone.ddd || "";
    const number = phone.number || "";

    if (!area && !number) return "";

    return `(${area}) ${number}`.trim();
  }
}

if (!customElements.get("custom-register-form")) {
  customElements.define("custom-register-form", CustomRegisterForm);
}
